/**
 * Live field references (ADR-0102 addendum).
 *
 * A value node with a `sourceKey` in action-log content (replaceText,
 * insertParagraph, insertClause) is a REFERENCE to a form field, not frozen
 * text: at replay it is re-resolved from the current compile / values, so
 * adapted text follows the form. A value node without a `sourceKey` is
 * literal and prints what was stored.
 *
 * Resolution order for a slot key:
 *
 *  1. the compiled base itself — the value node with that key in the node
 *     being edited, else in the same block instance, else anywhere (same
 *     `format` preferred). This is the block's own rendition (option label,
 *     suffix, number format), so a reference prints exactly what the
 *     standard text would.
 *  2. `values[key]`, printed by the field's type (`fields[key]`) or the
 *     stored node's `format` — for keys the compile does not print.
 *  3. the stored text (no values given: pre-addendum behaviour).
 *
 * An own field (`custom_…`) prints `values[key]` by its defined type. A
 * reference to an own field that is not defined becomes literal text
 * (`removedField`).
 */
import { createCompileContext } from '../block/context.js';
import { childLists } from './address.js';
import { customFieldValueFormat, formatCustomValue, textOfValue } from './fields.js';
import { isCustomFieldKey } from './types.js';
const DEFAULT_FORMAT = createCompileContext({ now: '1970-01-01T00:00:00.000Z' }).format;
/** Inline runs a block node owns directly (not its child blocks'). */
export function ownInlineRuns(node) {
    switch (node.type) {
        case 'paragraph':
        case 'heading':
            return [node.content];
        case 'optionList':
            return node.items.map((i) => i.content);
        case 'table':
            return [...node.columns.flatMap((c) => (c.header ? [c.header] : [])), ...(node.caption ? [node.caption] : [])];
        case 'detailList':
            return node.items.map((i) => i.term);
        case 'signature':
            return node.statement ? [node.statement] : [];
        default:
            return [];
    }
}
const valuesOf = (run, out = []) => {
    for (const n of run) {
        if (n.type === 'value')
            out.push(n);
        else if (n.type === 'strong' || n.type === 'emphasis' || n.type === 'label')
            valuesOf(n.content, out);
    }
    return out;
};
function indexValues(document) {
    const index = new Map();
    const visit = (list, instanceId) => {
        for (const node of list) {
            const inst = node.instanceId ?? instanceId;
            for (const run of ownInlineRuns(node)) {
                for (const value of valuesOf(run)) {
                    if (value.sourceKey == null)
                        continue;
                    const at = index.get(value.sourceKey) ?? [];
                    at.push({ value, owner: node, ...(inst ? { instanceId: inst } : {}) });
                    index.set(value.sourceKey, at);
                }
            }
            for (const { list: child } of childLists(node))
                visit(child, inst);
        }
    };
    for (const part of document.parts)
        for (const list of [part.letterhead ?? [], part.body, part.footer ?? []])
            visit(list, undefined);
    return index;
}
const optionText = (field, raw) => {
    const options = field?.options;
    if (!options || raw == null)
        return null;
    const hit = options.find((o) => o.code === raw || o.value === raw);
    return hit ? (hit.renderLabel ?? hit.value ?? hit.label) : null;
};
/** Prints a slot value without the block: by field type / option labels / stored format. */
function formatSlotValue(raw, field, stored, format) {
    const type = field?.type;
    if (type === 'money' || type === 'date' || type === 'boolean') {
        const out = formatCustomValue(type, raw, format);
        return { text: out.text, raw: out.raw };
    }
    if (type === 'select' || type === 'select_with_freetext') {
        const label = optionText(field, raw);
        if (label)
            return { text: label, raw: raw };
    }
    const as = type === 'currency' ? 'currency' : type === 'number' ? 'number' : stored.format;
    if (as === 'date')
        return { text: format.date(textOfValue(raw)), raw: textOfValue(raw) };
    if (as === 'currency' || as === 'number') {
        const out = formatCustomValue(as === 'currency' ? 'money' : 'number', raw, format);
        return { text: out.text, raw: out.raw };
    }
    if (typeof raw === 'boolean')
        return { text: raw ? 'Ja' : 'Nej', raw };
    if (Array.isArray(raw)) {
        const parts = raw.map((r) => optionText(field, r) ?? textOfValue(r)).filter((s) => !!s);
        return { text: parts.length ? parts.join(', ') : null, raw: null };
    }
    const text = textOfValue(raw);
    return { text, raw: text };
}
const pickOccurrence = (list, format) => list.find((o) => o.value.format === format) ?? list[0];
/** Builds the resolver over a compiled (pristine) document. Pure. */
export function createReferenceResolver(base, options = {}) {
    const format = options.format ?? DEFAULT_FORMAT;
    const live = options.live !== false;
    let index = null;
    const occurrences = (key) => {
        index ??= indexValues(base);
        return index.get(key) ?? [];
    };
    const values = options.values;
    const has = (key) => values != null && Object.prototype.hasOwnProperty.call(values, key);
    const fromBase = (key, stored, scope) => {
        const all = occurrences(key);
        if (all.length === 0)
            return undefined;
        const inNode = scope?.node ? all.filter((o) => o.owner === scope.node) : [];
        const inInstance = scope?.instanceId ? all.filter((o) => o.instanceId === scope.instanceId) : [];
        return (pickOccurrence(inNode, stored) ?? pickOccurrence(inInstance, stored) ?? pickOccurrence(all, stored))?.value;
    };
    const resolve = (value, scope) => {
        const key = value.sourceKey;
        if (!live || key == null)
            return { node: value };
        if (isCustomFieldKey(key)) {
            const custom = options.customFields;
            const def = custom?.get(key);
            if (custom && !def) {
                const { sourceKey: _k, ...literal } = value;
                return { node: literal, removedField: true };
            }
            if (!has(key) && !def)
                return { node: value };
            if (!values)
                return { node: value };
            const type = def?.type ?? (value.format === 'date' ? 'date' : value.format === 'currency' ? 'money' : value.format === 'number' ? 'number' : 'text');
            const out = formatCustomValue(type, values[key], format);
            return {
                node: {
                    type: 'value',
                    text: out.text,
                    ...(out.raw != null ? { raw: out.raw } : {}),
                    format: customFieldValueFormat(type),
                    blankWidth: value.blankWidth ?? 'medium',
                    sourceKey: key,
                },
            };
        }
        const hit = fromBase(key, value.format, scope);
        if (hit) {
            return {
                node: {
                    type: 'value',
                    text: hit.text,
                    ...(hit.raw !== undefined ? { raw: hit.raw } : {}),
                    ...(hit.format ? { format: hit.format } : {}),
                    ...((value.blankWidth ?? hit.blankWidth) ? { blankWidth: value.blankWidth ?? hit.blankWidth } : {}),
                    sourceKey: key,
                },
            };
        }
        if (has(key)) {
            const printed = formatSlotValue(values[key], options.fields?.[key], value, format);
            return {
                node: {
                    type: 'value',
                    text: printed.text,
                    ...(printed.raw != null ? { raw: printed.raw } : {}),
                    ...(value.format ? { format: value.format } : {}),
                    blankWidth: value.blankWidth ?? 'medium',
                    sourceKey: key,
                },
            };
        }
        return { node: value };
    };
    const resolveContent = (content, scope, onRemoved) => content.map((n) => {
        if (n.type === 'value') {
            const out = resolve(n, scope);
            if (out.removedField)
                onRemoved?.(n);
            return out.node;
        }
        if (n.type === 'strong' || n.type === 'emphasis' || n.type === 'label')
            return { ...n, content: resolveContent(n.content, scope, onRemoved) };
        return n;
    });
    const preview = (key) => {
        const def = options.customFields?.get(key);
        const field = options.fields?.[key];
        const known = def != null || field != null || occurrences(key).length > 0 || has(key);
        if (!known)
            return undefined;
        const stub = { type: 'value', text: null, sourceKey: key, ...(def ? { format: customFieldValueFormat(def.type) } : {}) };
        const out = resolve(stub).node;
        return { text: out.text, ...(out.format ? { format: out.format } : {}) };
    };
    return { resolve, resolveContent, preview };
}
//# sourceMappingURL=references.js.map